import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  API,
  readStoredStatus,
  showError,
  showInfo,
  showSuccess,
} from '../helpers';
import Turnstile from 'react-turnstile';
import { ThemeContext } from '../context/Theme';
import Button from './ui/Button';
import Modal from './ui/Modal';
import Input from './ui/Input';
import Card from './ui/Card';

const PersonalSetting = () => {
  const [themeState] = useContext(ThemeContext);
  const [inputs, setInputs] = useState({
    email_verification_code: '',
    email: '',
    totp_code: '',
  });
  const [status, setStatus] = useState({});
  const [showEmailBindModal, setShowEmailBindModal] = useState(false);
  const [turnstileEnabled, setTurnstileEnabled] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [totpStatus, setTotpStatus] = useState({ enabled: false });
  const [totpSetup, setTotpSetup] = useState(null);

  useEffect(() => {
    let status = readStoredStatus();
    if (status) {
      setStatus(status);
      if (status.turnstile_check) {
        setTurnstileEnabled(true);
        setTurnstileSiteKey(status.turnstile_site_key);
      }
    }
  }, []);

  useEffect(() => {
    const loadTotpStatus = async () => {
      const res = await API.get('/api/user/totp');
      const { success, message, data } = res.data;
      if (success) {
        setTotpStatus(data || { enabled: false });
      } else {
        showError(message);
      }
    };
    loadTotpStatus();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const openGitHubOAuth = () => {
    window.open(
      `https://github.com/login/oauth/authorize?client_id=${status.github_client_id}&scope=user:email`
    );
  };

  const sendVerificationCode = async () => {
    if (inputs.email === '') return;
    if (turnstileEnabled && turnstileToken === '') {
      showInfo('请稍后几秒重试，Turnstile 正在检查用户环境！');
      return;
    }
    setLoading(true);
    const res = await API.get(
      `/api/verification?email=${inputs.email}&turnstile=${turnstileToken}`
    );
    const { success, message } = res.data;
    if (success) {
      showSuccess('验证码发送成功，请检查邮箱！');
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const bindEmail = async () => {
    if (inputs.email_verification_code === '') return;
    setLoading(true);
    const res = await API.get(
      `/api/oauth/email/bind?email=${inputs.email}&code=${inputs.email_verification_code}`
    );
    const { success, message } = res.data;
    if (success) {
      showSuccess('邮箱账户绑定成功！');
      setShowEmailBindModal(false);
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const initTotp = async () => {
    setLoading(true);
    const res = await API.post('/api/user/totp/init');
    const { success, message, data } = res.data;
    if (success) {
      setTotpSetup(data);
      setTotpStatus({ enabled: false });
      showSuccess('已生成新的两步验证密钥');
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const enableTotp = async () => {
    if (!inputs.totp_code) {
      showInfo('请输入 6 位验证码');
      return;
    }
    setLoading(true);
    const res = await API.post('/api/user/totp/enable', {
      code: inputs.totp_code,
    });
    const { success, message } = res.data;
    if (success) {
      setTotpStatus((prev) => ({ ...prev, enabled: true }));
      setTotpSetup(null);
      setInputs((prev) => ({ ...prev, totp_code: '' }));
      showSuccess('两步验证已启用');
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const disableTotp = async () => {
    if (!inputs.totp_code) {
      showInfo('请输入当前 6 位验证码');
      return;
    }
    setLoading(true);
    const res = await API.post('/api/user/totp/disable', {
      code: inputs.totp_code,
    });
    const { success, message } = res.data;
    if (success) {
      setTotpStatus({ enabled: false });
      setTotpSetup(null);
      setInputs((prev) => ({ ...prev, totp_code: '' }));
      showSuccess('两步验证已关闭');
    } else {
      showError(message);
    }
    setLoading(false);
  };

  return (
    <div
      style={{
        maxWidth: '800px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
      }}
    >
      <Card padding='1.5rem'>
        <h3
          style={{
            fontSize: '1.1rem',
            fontWeight: 'bold',
            marginBottom: '0.5rem',
          }}
        >
          个人设置
        </h3>
        <p
          style={{
            fontSize: '0.875rem',
            color: 'var(--text-secondary)',
            marginBottom: '1rem',
          }}
        >
          管理个人资料与账号安全设置。
        </p>
        <div className='settings-action-grid'>
          <Link to={`/user/edit/`}>
            <Button variant='secondary'>更新个人信息</Button>
          </Link>
        </div>
      </Card>

      <Card padding='1.5rem'>
        <h3
          style={{
            fontSize: '1.1rem',
            fontWeight: 'bold',
            marginBottom: '0.5rem',
          }}
        >
          两步验证
        </h3>
        <p
          style={{
            fontSize: '0.875rem',
            color: 'var(--text-secondary)',
            marginBottom: '1rem',
          }}
        >
          使用 TOTP 验证器保护后台登录。当前状态：
          {totpStatus.enabled ? '已启用' : '未启用'}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {!totpStatus.enabled && (
            <Button variant='secondary' onClick={initTotp} disabled={loading}>
              生成两步验证密钥
            </Button>
          )}
          {totpSetup && (
            <div
              style={{
                padding: '1rem',
                border: '1px solid var(--border-color)',
                borderRadius: '0.75rem',
                background: 'var(--bg-secondary)',
                wordBreak: 'break-all',
              }}
            >
              <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
                    totpSetup.otpauth
                  )}`}
                  alt='TOTP QR Code'
                  style={{
                    width: '220px',
                    height: '220px',
                    maxWidth: '100%',
                    borderRadius: '0.75rem',
                    border: '1px solid var(--border-color)',
                    background: '#fff',
                    padding: '0.5rem',
                  }}
                />
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                密钥：<code>{totpSetup.secret}</code>
              </div>
              <div style={{ marginBottom: '0.75rem' }}>
                OTP URI：<code>{totpSetup.otpauth}</code>
              </div>
              <Input
                label='验证码'
                name='totp_code'
                placeholder='输入验证器里当前的 6 位验证码'
                value={inputs.totp_code}
                onChange={handleInputChange}
              />
              <Button variant='primary' onClick={enableTotp} disabled={loading}>
                启用两步验证
              </Button>
            </div>
          )}
          {totpStatus.enabled && (
            <div
              style={{
                padding: '1rem',
                border: '1px solid var(--border-color)',
                borderRadius: '0.75rem',
                background: 'var(--bg-secondary)',
              }}
            >
              <Input
                label='验证码'
                name='totp_code'
                placeholder='输入当前 6 位验证码以关闭'
                value={inputs.totp_code}
                onChange={handleInputChange}
              />
              <Button variant='outline' onClick={disableTotp} disabled={loading}>
                关闭两步验证
              </Button>
            </div>
          )}
        </div>
      </Card>

      <Card padding='1.5rem'>
        <h3
          style={{
            fontSize: '1.1rem',
            fontWeight: 'bold',
            marginBottom: '0.5rem',
          }}
        >
          账号绑定
        </h3>
        <p
          style={{
            fontSize: '0.875rem',
            color: 'var(--text-secondary)',
            marginBottom: '1rem',
          }}
        >
          可选绑定 GitHub 与邮箱，便于登录和找回能力。
        </p>
        <div className='settings-action-grid'>
          {status.github_oauth && (
            <Button variant='secondary' onClick={openGitHubOAuth}>
              绑定 GitHub 账户
            </Button>
          )}
          <Button
            variant='secondary'
            onClick={() => setShowEmailBindModal(true)}
          >
            绑定邮箱地址
          </Button>
        </div>
      </Card>

      <Modal
        title='绑定邮箱地址'
        isOpen={showEmailBindModal}
        onClose={() => setShowEmailBindModal(false)}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Input
              placeholder='输入邮箱地址'
              onChange={handleInputChange}
              name='email'
              type='email'
              style={{ marginBottom: 0, flex: 1 }}
            />
            <Button
              onClick={sendVerificationCode}
              disabled={loading}
              variant='outline'
            >
              获取验证码
            </Button>
          </div>
          <Input
            placeholder='验证码'
            name='email_verification_code'
            value={inputs.email_verification_code}
            onChange={handleInputChange}
          />
          {turnstileEnabled && (
            <div style={{ margin: '1rem 0' }}>
              <Turnstile
                sitekey={turnstileSiteKey}
                theme={themeState.theme}
                onVerify={(token) => setTurnstileToken(token)}
              />
            </div>
          )}
          <Button
            variant='primary'
            className='w-full'
            onClick={bindEmail}
            disabled={loading}
          >
            绑定
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default PersonalSetting;
